import { bound } from "@frui.ts/helpers";
import { HttpTransportType, HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { plainToClass } from "class-transformer";
import { notificationWsHub } from "config";
import * as Sentry from "@sentry/react";
import { action, observable } from "mobx";
import ChargeOrderResult from "models/chargeOrderResult";
import AuthorizationService from "services/authorizationService";
import { ContactEditationTriggerer } from "../../../models/contact";
import Validity from "../../../models/validity";
import Vehicle from "../../../models/vehicle";
import WSError from "../../../models/WSError";
import EventBus, {
  emailUpdated,
  googlePayPayment3DSecureLoaded,
  googlePayPaymentStatusChanged,
  putChargeContactFailure,
  putChargeContactSuccess,
  putChargeValidityFailure,
  putChargeValiditySuccess,
  putChargeVehicleFailure,
  putChargeVehicleSuccess,
} from "../../eventBus";
import {
  GET_CONNECTION_ID_INVOKE,
  GET_GOOGLE_PAY_STATUS,
  NOTIFICATION_SERVICE_CHARGE_ORDER_FAILURE,
  POST_CHARGE_ORDER_RESULT,
  PUT_CHARGE_CONTACT_RESULT,
  PUT_CHARGE_VALIDITY_RESULT,
  PUT_CHARGE_VEHICLE_DURING_VALIDITY_RESULT,
  PUT_CHARGE_VEHICLE_RESULT,
  SIGNAL_R_CONNECTION_ID_HEADER_NAME,
} from "./constants";
import GooglePayProcessPaymentResponse from "models/googlePayProcessPaymentResponse";
import ChargeOrderResponse from "entities/chargeOrderResponse";
import PutChargeContactResult from "entities/putChargeContactResult";

export default class SignalRNotificationService {
  constructor(private authorizationService: AuthorizationService, private eventBus: EventBus) {}
  notificationHub?: HubConnection;

  @observable connectionId?: string | null;

  @action.bound
  async initialize() {
    await this.setupSignalRConnection();
  }

  setupSignalRConnection = async () => {
    if (!notificationWsHub) {
      // TODO: fire event
      return;
    }

    const token = this.authorizationService.token()?.accessToken;

    if (!token) return;

    this.notificationHub = new HubConnectionBuilder()
      .withUrl(notificationWsHub, {
        accessTokenFactory: () => token,
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .build();
  };

  serviceStart = async () => {
    try {
      await this.notificationHub?.start();

      await this.getConnectionId();
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag("websocket", "serviceStart");
        Sentry.captureException(error);
      });
      throw error;
    }
  };

  serviceStop = async () => {
    if (!this.connectionId) return;

    try {
      await this.notificationHub?.stop();
      this.connectionId = undefined;
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag("websocket", "serviceStop");
        Sentry.captureException(error);
      });
      throw error;
    }
  };

  subscribeToPostChargeOrderResult = (handle: (chargeOrder?: ChargeOrderResponse, error?: string) => void) => {
    this.notificationHub?.on(POST_CHARGE_ORDER_RESULT, (response: ChargeOrderResult) => {
      if (response.result) {
        handle(response.result, undefined);
      } else {
        handle(undefined, response.error?.type || NOTIFICATION_SERVICE_CHARGE_ORDER_FAILURE);
      }
    });
  };

  subscribeToPutChargeContactResult = (triggerer: ContactEditationTriggerer) =>
    this.notificationHub?.on(PUT_CHARGE_CONTACT_RESULT, (response: any) => {
      let parsedResponse: PutChargeContactResult | WSError;

      if (response.hasOwnProperty("result")) {
        parsedResponse = plainToClass(PutChargeContactResult, response.result);
      } else {
        parsedResponse = plainToClass(WSError, response);
      }

      if (parsedResponse instanceof WSError) {
        this.eventBus.publish(putChargeContactFailure({ type: parsedResponse.error.type, triggerer }));
      } else {
        this.eventBus.publish(
          putChargeContactSuccess({
            authCode: parsedResponse.authCode,
            emailAddress: parsedResponse.emailAddress,
            notificationPreferences: parsedResponse.notificationPreferences,
            triggerer,
          })
        );
        if (parsedResponse.emailAddress) {
          this.eventBus.publish(emailUpdated({ emailAddress: parsedResponse.emailAddress }));
        }
      }
    });

  subscribeToPutChargeValidityResult = (emailAddress?: string) =>
    this.notificationHub?.on(PUT_CHARGE_VALIDITY_RESULT, (response: any) => {
      let parsedResponse: Validity | WSError;

      if (response.hasOwnProperty("result")) {
        parsedResponse = plainToClass(Validity, response.result);
      } else {
        parsedResponse = plainToClass(WSError, response);
      }

      if (parsedResponse instanceof WSError) {
        this.eventBus.publish(putChargeValidityFailure({ type: parsedResponse.error.type }));
      } else {
        this.eventBus.publish(putChargeValiditySuccess({ validity: parsedResponse }));
        if (emailAddress) {
          this.eventBus.publish(emailUpdated({ emailAddress }));
        }
      }
    });

  @bound
  subscribeToPutChargeVehicleResult(emailAddress?: string) {
    return this.notificationHub?.on(PUT_CHARGE_VEHICLE_RESULT, (response: any) => {
      let parsedResponse: Vehicle | WSError;

      if (response.hasOwnProperty("result")) {
        parsedResponse = plainToClass(Vehicle, response.result);
      } else {
        parsedResponse = plainToClass(WSError, response);
      }

      if (parsedResponse instanceof WSError) {
        this.eventBus.publish(putChargeVehicleFailure({ type: parsedResponse.error.type }));
      } else {
        this.eventBus.publish(putChargeVehicleSuccess({ vehicle: parsedResponse }));
        if (emailAddress) {
          this.eventBus.publish(emailUpdated({ emailAddress }));
        }
      }
    });
  }

  @bound
  subscribeToPutChargeVehicleChangesDuringValidityResult(emailAddress?: string) {
    return this.notificationHub?.on(PUT_CHARGE_VEHICLE_DURING_VALIDITY_RESULT, (response: any) => {
      let parsedResponse: Vehicle | WSError;

      if (response.hasOwnProperty("result")) {
        parsedResponse = plainToClass(Vehicle, response.result);
      } else {
        parsedResponse = plainToClass(WSError, response);
      }

      if (parsedResponse instanceof WSError) {
        this.eventBus.publish(putChargeVehicleFailure({ type: parsedResponse.error.type }));
      } else {
        this.eventBus.publish(putChargeVehicleSuccess({ vehicle: parsedResponse }));
        if (emailAddress) {
          this.eventBus.publish(emailUpdated({ emailAddress }));
        }
      }
    });
  }

  subscribeToGetGooglePayStatus = () => {
    return this.notificationHub?.on(GET_GOOGLE_PAY_STATUS, (response: { result: GooglePayProcessPaymentResponse }) => {
      this.eventBus.publish(googlePayPaymentStatusChanged({ status: response.result.paymentStatus }));

      if (response.result.action?.browserChallenge?.challengeUrl) {
        this.eventBus.publish(googlePayPayment3DSecureLoaded({ url: response.result.action.browserChallenge.challengeUrl }));
      }
    });
  };

  buildHeaders(): { [key: string]: string } {
    const header = this.connectionIdHeader();
    const headers: { [key: string]: any } = {};
    if (header) {
      headers[SIGNAL_R_CONNECTION_ID_HEADER_NAME] = header;
    }

    return headers;
  }

  getConnectionId = async () => {
    this.connectionId = await this.notificationHub?.invoke(GET_CONNECTION_ID_INVOKE);
  };

  private connectionIdHeader = () => (this.connectionId ? this.connectionId : undefined);
}
